import React, { useState, useRef, useEffect, useContext } from "react";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { putClient } from "../API/clientAPI";
import { putJobOrder } from "../API/jobOrderAPI";
import { getClient } from "../API/clientAPI";
import { getTechnicians } from "../API/technicianAPI";
import { getJobOrder } from "../API/jobOrderAPI";
import { sendEmailQueue } from "../API/emailAPI";
import { submitDocumentFieldMapper } from "../API/documentFieldMapperAPI";

import TermiteLocationCard from "./FormComponents/TermiteLocationCard";
import ExterraCard from "./FormComponents/ExterraCard";
import StepHeaderCard from "./FormComponents/StepHeaderCard";
import StepActionBar from "./FormComponents/StepActionBar";
import PageOneReviewSection from "./FormComponents/PageOneReviewSection";
import PaymentDetailsSection from "./FormComponents/PaymentDetailsSection";
import TechnicianSignatureCard from "./FormComponents/TechnicianSignatureCard";
import EditTechniciansDialog from "./FormComponents/EditTechniciansDialog";
import ClientSignatureCard from "./FormComponents/ClientSignatureCard";
import ClientSatisfactionCard from "./FormComponents/ClientSatisfactionCard";
import DocumentLinksCard from "./FormComponents/DocumentLinksCard";
import { buildServiceReportEmail } from "./FormComponents/email/buildServiceReportEmail";
import { buildGeneratedDocumentLinks } from "./FormComponents/email/buildGeneratedDocumentLinks";

import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

// Import S3 configurations and bucket names
import {
  s3TempBucketUploader,
  TEMP_BUCKET_NAME,
} from "../Promotions/config/aws-config-temp-bucket";

import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

function TermiteControlForm() {
  const { id } = useParams();
  const [jobOrder, setJobOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  const [jobOrderParameters, setjobOrderParameters] = useState({
    status: "",
    inspection_report: [],
    chemical_used: [],
    equipment_used: [],
    service_fee: "",
    service_report_payment_status: [],
    service_report_payment_method: [],
    job_date: "",
    start_time: "",
    accomplished_by_technician: "",
    reminder_notes: "",
    satisfaction_rating: 0,
  });

  const [client, setClient] = useState({});
  const [editedClient, setEditedClient] = useState();
  const [links, setLinks] = useState({});

  // all techs for the dialog
  const [allTechnicians, setAllTechnicians] = useState([]);
  const [editTechsOpen, setEditTechsOpen] = useState(false);
  const [editedTechs, setEditedTechs] = useState([]);

  const [technicianSignatory, setTechnicianSignatory] = useState("");
  const [satisfactionRating, setSatisfactionRating] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);

  const STEP_INDEX = {
    REVIEW_JOB_DETAILS: 0,
    INSPECTION_REPORT: 1,
    RECOMMENDATIONS_AND_NOTES: 2,
    PAYMENT_DETAILS: 3,
    TECHNICIAN_ONLY: 4,
    CLIENT_SIGNATURE: 5,
    CLIENT_SATISFACTION: 6,
    FINAL_REVIEW: 7,
  };

  const reportGenerated = Boolean(
    links?.receipts_url || links?.certificate_url || links?.reports_url
  );

  const RECOMMENDATIONS = [
    "Schedule termite control",
    "Monitor regularly",
    "Seal entry points with caulking or foam",
    "Improve airflow, reduce moisture",
    "Repair leaks, dry affected zones",
    "Stagnant water / leaking pipes",
    "Relocate or elevate storage",
    "Closed room or area Inaccessible",
    "Ensure room access for full inspection",
  ];

  // --- Inspection Report (locations) schema helpers ---
  const isBlank = (v) => v == null || String(v).trim() === "";
  
  // A row is "empty" if user didn't touch anything
  const rowIsCompletelyEmpty = (row) =>
    isBlank(row.location) &&
    isBlank(row.location_other) &&
    isBlank(row.findings) &&
    isBlank(row.action_taken) &&
    isBlank(row.bait_monitor_location) &&
    isBlank(row.remarks);
  
  const LocationRowSchema = z.object({
    location: z.string().optional().transform(v => v ?? ""),
    location_other: z.string().optional().transform(v => v ?? ""),
    findings: z.string().optional().transform(v => v ?? ""),
    action_taken: z.string().optional().transform(v => v ?? ""),
    bait_monitor_location: z.string().optional().transform(v => v ?? ""),
    remarks: z.string().optional().transform(v => v ?? ""),
  }).superRefine((row, ctx) => {
    // allow fully empty rows (so users can leave extra cards blank)
    if (rowIsCompletelyEmpty(row)) return;
  
    // required core fields
    if (isBlank(row.location)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["location"], message: "Location is required" });
    }
    if (isBlank(row.findings)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["findings"], message: "Findings is required" });
    }
    if (isBlank(row.action_taken)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["action_taken"], message: "Action Taken is required" });
    }
    if (isBlank(row.remarks)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["remarks"], message: "Remarks is required" });
    }
    // conditional requirements
    if (row.location === "Other" && isBlank(row.location_other)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["location_other"], message: "Please specify other location" });
    }
    if (row.action_taken === "baiting" && isBlank(row.bait_monitor_location)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["bait_monitor_location"], message: "Please specify bait/monitor location" });
    }
  });

  const TermiteSchema = z.object({
    satisfaction_rating: z.coerce
      .number({
        required_error: "Please pick a rating (1–5).",
        invalid_type_error: "Please pick a rating (1–5).",
      })
      .min(1, { message: "Please pick a rating (1–5)." })
      .max(5, { message: "Please pick a rating (1–5)." }),
    acknowledgedSignature: z.string().min(1, "Please click Upload for Acknowledged signature."),
    accomplishedSignature: z.string().min(1, "Please click Upload for Accomplished signature."),
    acknowledgedUploaded: z.boolean().refine(v => v === true, "Please click Upload for Acknowledged signature."),
    accomplishedUploaded: z.boolean().refine(v => v === true, "Please click Upload for Accomplished signature."),
    technician_signatory: z.string().trim().min(1, "Please choose a technician signatory."),
    acknowledgedClientSignatory: z.string().trim().min(1, "Please enter the client representative."),
    status: z
      .string()
      .refine((v) => v && v !== "Not Done", {
        message: 'Please set Job Order Status to something other than "Not Done".',
    }),
    // Inspection Report rows
    locations: z.array(LocationRowSchema)
      .min(1, "Add at least one location")
      .superRefine((rows, ctx) => {
        // At least one FULL row must be present (not empty + passes requireds)
        const fullRows = rows.filter(r =>
          !rowIsCompletelyEmpty(r) &&
          !isBlank(r.location) &&
          !isBlank(r.findings) &&
          !isBlank(r.action_taken) &&
          !isBlank(r.remarks)
        );
        if (fullRows.length === 0) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: [0, "location"], message: "Please complete at least one Inspection Report row." });
        }
      }),
    termite_recommendations: z
      .array(z.string())
      .min(1, "Please select at least one recommendation."),
  }).passthrough();


  const {
    control,
    register,
    handleSubmit,
    setValue,
    clearErrors,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(TermiteSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      // required fields
      satisfaction_rating: 0,
      acknowledgedSignature: "",
      accomplishedSignature: "",
      acknowledgedUploaded: false,
      accomplishedUploaded: false,
      technician_signatory: "",
      acknowledgedClientSignatory: "",
      status: "",
      // everything else that belongs to this form
      locations: [
        {
          location: "",
          location_other: "",
          findings: "",
          action_taken: "",
          bait_monitor_location: "",
          remarks: "",
          termite_activity: "",
          termite_recommendations: [],
          exterra_bait_stations: [],
        },
      ],
      termite_recommendations: [],
      exterra_bait_stations: [],
    },
  });

  const saveAndUploadAccomplishedSignature = async () => {
    try {
      setIsLoading(true);

      // ⏰ CAPTURE TECHNICIAN SIGNATURE UPLOAD TIMESTAMP
      const uploadTimestamp = new Date().toISOString();
      setTechnicianSignatureUploadedAt(uploadTimestamp);

      const url = await resizeAndUploadSignature(
        accomplishedBySignatureRef,
        jobOrder.id,
        "accomplished"
      );
      setAccomplishedSignatureURL(url); // keep your state
      // ✅ write into the form and mark as uploaded
      setValue("accomplishedSignature", url, { shouldValidate: true, shouldDirty: true });
      setValue("accomplishedUploaded", true, { shouldValidate: true });

      // 💾 IMMEDIATELY SAVE TIMESTAMP TO BACKEND (to prevent cheating)
      await putJobOrder(jobOrder.id, {
        technician_signature_uploaded_at: uploadTimestamp
      });
      // Timestamp saved (hidden from logs for security)

      setSuccessMessage("Signature successfully saved!");
    } catch (error) {
      console.error("Error saving signature:", error);
      setErrorMessage(`Error saving signature: ${error.message}`);
      // ensure invalid if upload failed
      setValue("accomplishedSignature", "", { shouldValidate: true });
      setValue("accomplishedUploaded", false, { shouldValidate: true });
    } finally {
      setIsLoading(false);
    }
  };

  const clearAccomplishedSignature = () => {
    accomplishedBySignatureRef.current?.clear();
    setAccomplishedSignatureURL("");
    setValue("accomplishedSignature", "", { shouldValidate: true, shouldDirty: true });
    setValue("accomplishedUploaded", false, { shouldValidate: true });
  };

  const saveAndUploadAcknowledgedSignature = async () => {
    try {
      setIsLoading(true);
      const url = await resizeAndUploadSignature(
        acknowledgedBySignatureRef,
        jobOrder.id,
        "acknowledged"
      );

      // ⏰ CAPTURE CLIENT SIGNATURE UPLOAD TIMESTAMP
      const uploadTimestamp = new Date().toISOString();
      setClientSignatureUploadedAt(uploadTimestamp);

      setAcknowledgedSignatureURL(url);
      // ✅ write into the form and mark as uploaded
      setValue("acknowledgedSignature", url, { shouldValidate: true, shouldDirty: true });
      setValue("acknowledgedUploaded", true, { shouldValidate: true });

      // 💾 IMMEDIATELY SAVE TIMESTAMP TO BACKEND (to prevent cheating)
      await putJobOrder(jobOrder.id, {
        client_signature_uploaded_at: uploadTimestamp
      });
      // Timestamp saved (hidden from logs for security)

      setSuccessMessage("Signature successfully saved!");
    } catch (error) {
      console.error("Error saving signature:", error);
      setErrorMessage("Error saving signature. Please try again.");
      setValue("acknowledgedSignature", "", { shouldValidate: true });
      setValue("acknowledgedUploaded", false, { shouldValidate: true });
    } finally {
      setIsLoading(false);
    }
  };

  const clearAcknowledgedSignature = () => {
    acknowledgedBySignatureRef.current?.clear();
    setAcknowledgedSignatureURL("");
    setValue("acknowledgedSignature", "", { shouldValidate: true, shouldDirty: true });
    setValue("acknowledgedUploaded", false, { shouldValidate: true });
  };

  const {
    fields: locationFields,
    append: appendLocation,
    remove: removeLocation,
  } = useFieldArray({ control, name: "locations" });

  const navigate = useNavigate();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get("returnTo");

  const handleCancel = () => {
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate("/manage-service-reports");
    }
  };

  const handleClose = () => {
    navigate("/manage-service-reports");
  };

  const goBackToStep = (stepIndex) => {
    setCurrentStep(stepIndex);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        setJobOrder(data || {});
        setSatisfactionRating(data?.satisfaction_rating || 0);
        const client = await getClient(data.client_id); // Fetch client using client_id from job order
        setClient(client);

        const technician_signed = data?.technicians.map(({ firstname, lastname }) => `${firstname} ${lastname}`);

        setjobOrderParameters((prev) => ({
          ...prev,
          status: data?.status || "",
          inspection_report:  data?.inspection_report || [],
          job_date: data?.job_date || "",
          start_time: data?.start_time || "",
          accomplished_by_technician: technician_signed || []
        }));

        setValue("status", data?.status || "", { shouldValidate: true });
        setEditedClient(data.client);
      } catch (error) {
        setErrorMessage("Error fetching job order:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchJobOrder();
  }, [id]);


  // for edit tech dialog
  useEffect(() => {
    // fetch once
    getTechnicians().then(data => setAllTechnicians(data || [])).catch(console.error);
  }, []);

  const openEditTechs = () => {
    const ids = (jobOrder?.technicians || []).map((t) => t.id).filter(Boolean);
    setEditedTechs(ids.length > 0 ? ids : [""]);
    setEditTechsOpen(true);
  };

  const closeEditTechs = () => setEditTechsOpen(false);

  const handleChangeEditedTech = (index, techId) => {
    setEditedTechs((prev) => prev.map((id, i) => (i === index ? techId : id)));
  };

  const handleAddEditedTech = () => {
    setEditedTechs((prev) => [...prev, ""]);
  };

  const handleRemoveEditedTech = (index) => {
    setEditedTechs((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [""];
    });
  };

  const saveEditTechs = async () => {
    const uniqueIds = [...new Set(editedTechs.filter(Boolean))];

    const selected = (allTechnicians || [])
      .filter((t) => uniqueIds.includes(t.id))
      .map(({ id, firstname, lastname }) => ({ id, firstname, lastname }));

    try {
      await putJobOrder(jobOrder.id, { technicians: selected });

      setJobOrder((prev) => ({ ...prev, technicians: selected }));

      setjobOrderParameters((prev) => ({
        ...prev,
        accomplished_by_technician: selected.map(
          (t) => `${t.firstname} ${t.lastname}`
        ),
      }));

      const validNames = new Set(
        selected.map((t) => `${t.firstname} ${t.lastname}`)
      );

      const nextSignatory =
        technicianSignatory && validNames.has(technicianSignatory)
          ? technicianSignatory
          : "";

      setTechnicianSignatory(nextSignatory);
      setValue("technician_signatory", nextSignatory, {
        shouldValidate: true,
        shouldDirty: true,
      });

      setSuccessMessage("Technicians updated.");
      setEditTechsOpen(false);
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to update technicians.");
    }
  };
  
  // Add new states for holding the signature data URLs
  const accomplishedBySignatureRef = useRef(null);
  const acknowledgedBySignatureRef = useRef(null);
  // Declare state for each signature URL
  const [accomplishedSignatureURL, setAccomplishedSignatureURL] = useState("");
  const [acknowledgedSignatureURL, setAcknowledgedSignatureURL] = useState("");

  const [acknowledgedClientSignatory, setAcknowledgedClientSignatory] = useState("");

  // Track when client signature was uploaded (to catch technician delays)
  const [clientSignatureUploadedAt, setClientSignatureUploadedAt] = useState(null);
  // Track when technician signature was uploaded
  const [technicianSignatureUploadedAt, setTechnicianSignatureUploadedAt] = useState(null);

  // Function to recursively convert true/false to True/False in an object
  const convertBooleans = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map((item) => convertBooleans(item));
    } else if (obj && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, convertBooleans(value)])
      );
    } else if (typeof obj === "boolean") {
      return obj ? "True" : "False"; // Convert true/false to 'True'/'False'
    }
    return obj;
  };

  const handleJObOrderChange = (field, value) => {
    setjobOrderParameters(prev => ({ ...prev, [field]: value }));
    if (field === "status") {
      setValue("status", value, { shouldValidate: true, shouldDirty: true });
      if (value !== "Not Done") clearErrors("status");
    }
  };

  const onInvalid = () => {
    const first = document.querySelector(".MuiFormHelperText-root.Mui-error");
    first?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const buildStepLogEntry = (stepKey, stepLabel, timestamp) => ({
    step_key: stepKey,
    step_label: stepLabel,
    action: "NEXT_CLICKED",
    timestamp,
  });

  const logReportStep = async (stepKey, stepLabel) => {
    if (!jobOrder?.id) {
      throw new Error("Missing job order id.");
    }

    const timestamp = new Date().toISOString();

    const existingLogs = Array.isArray(jobOrder?.report_page_logs)
      ? jobOrder.report_page_logs
      : [];

    const nextLogs = [
      ...existingLogs,
      buildStepLogEntry(stepKey, stepLabel, timestamp),
    ];

    const payload = {
      report_page_logs: nextLogs,
      [`${stepKey}_completed_at`]: timestamp,
    };

    await putJobOrder(jobOrder.id, payload);

    setJobOrder((prev) => ({
      ...prev,
      ...payload,
    }));

    return timestamp;
  };

  const goToStep = async ({
    currentStepKey,
    currentStepLabel,
    nextStepIndex,
    fieldsToValidate = [],
  }) => {
    try {
      setIsLoading(true);
      setErrorMessage("");

      if (fieldsToValidate.length > 0) {
        const isValid = await trigger(fieldsToValidate);

        if (!isValid) {
          setErrorMessage("Please complete the required fields before proceeding.");
          onInvalid();
          return;
        }
      }

      await logReportStep(currentStepKey, currentStepLabel);

      setCurrentStep(nextStepIndex);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error(`Failed to log step: ${currentStepKey}`, error);
      setErrorMessage("Failed to proceed to the next page. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextFromReviewJobDetails = async () => {
    await goToStep({
      currentStepKey: "review_job_details",
      currentStepLabel: "REVIEW JOB DETAILS",
      nextStepIndex: STEP_INDEX.INSPECTION_REPORT,
      fieldsToValidate: ["status"],
    });
  };

  const handleNextFromInspectionReport = async () => {
    await goToStep({
      currentStepKey: "inspection_report",
      currentStepLabel: "INSPECTION REPORT",
      nextStepIndex: STEP_INDEX.RECOMMENDATIONS_AND_NOTES,
      fieldsToValidate: ["locations"],
    });
  };

  const handleNextFromRecommendationsAndNotes = async () => {
    await goToStep({
      currentStepKey: "service_details",
      currentStepLabel: "RECOMMENDATIONS AND NOTES",
      nextStepIndex: STEP_INDEX.PAYMENT_DETAILS,
      fieldsToValidate: ["termite_recommendations"],
    });
  };

  const handleNextFromPaymentDetails = async () => {
    await goToStep({
      currentStepKey: "payment_details",
      currentStepLabel: "PAYMENT DETAILS",
      nextStepIndex: STEP_INDEX.TECHNICIAN_ONLY,
      fieldsToValidate: [],
    });
  };

  const handleNextFromTechnicianOnly = async () => {
    await goToStep({
      currentStepKey: "technician_confirmation",
      currentStepLabel: "FOR TECHNICIANS ONLY",
      nextStepIndex: STEP_INDEX.CLIENT_SIGNATURE,
      fieldsToValidate: [
        "technician_signatory",
        "accomplishedSignature",
        "accomplishedUploaded",
      ],
    });
  };

  const handleNextFromClientSignature = async () => {
    await goToStep({
      currentStepKey: "client_signature",
      currentStepLabel: "FOR CLIENT ONLY - SIGNATURE",
      nextStepIndex: STEP_INDEX.CLIENT_SATISFACTION,
      fieldsToValidate: [
        "acknowledgedClientSignatory",
        "acknowledgedSignature",
        "acknowledgedUploaded",
      ],
    });
  };

  const handleNextFromClientSatisfaction = async () => {
    await goToStep({
      currentStepKey: "client_satisfaction",
      currentStepLabel: "FOR CLIENT ONLY - SATISFACTION",
      nextStepIndex: STEP_INDEX.FINAL_REVIEW,
      fieldsToValidate: ["satisfaction_rating"],
    });
  };

  // 1. derive a lookup from the raw arrays for label ↔ value mapping
  const formatKey = s => s.toLowerCase().replace(/\s+/g, "_");

  // 2. filter & sort selected job types
  const selectedJobTypes = (jobOrder?.job_types || [])
    // map back to the human-readable label
    .map(val => JOB_TYPES.find(opt => formatKey(opt) === val))
    // remove any non-matches
    .filter(Boolean)
    // alphabetical
    .sort((a, b) => a.localeCompare(b));

  // 3. same for descriptions
  const selectedJobDescriptions = (jobOrder?.job_description || [])
    .map(val => JOB_DESCRIPTIONS.find(opt => formatKey(opt) === val))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  const [chemical_used, setChemicalUsed] = useState([
    { label: "1", chemical: "", dilution: "" },
    { label: "2", chemical: "", dilution: "" },
    { label: "3", chemical: "", dilution: "" },
    { label: "4", chemical: "", dilution: "" },
    { label: "5", chemical: "", dilution: "" },
    { label: "6", chemical: "", dilution: "" },
  ]);

  const handleChemicalInputChange = (index, field, value) => {
    const newChemicalUsed = [...chemical_used];
    newChemicalUsed[index][field] = value;
    setChemicalUsed(newChemicalUsed);
  };

  const [equipment_used, setEquipmentUsed] = useState([
    { label: "Hand Sprayer", usage: false },
    { label: "Power Sprayer", usage: false }
  ]);

  const handleEquipmentCheckboxChange = (index) => {
    const newEquipmentUsed = [...equipment_used];
    newEquipmentUsed[index].usage = !newEquipmentUsed[index].usage;
    setEquipmentUsed(newEquipmentUsed);
  };

  const [paymentStatus, setPaymentStatus] = useState([
    { label: "Paid", selection: false },
    { label: "COD", selection: false },
    { label: "For Collection", selection: false },
  ]);

  const [paymentMethod, setPaymentMethod] = useState([
    { label: "Cash", selection: false },
    { label: "Check", selection: false },
    { label: "Free", selection: false },
  ]);

  const handlePaymentStatusChange = (statusIndex) => {
    const newStatusOptions = paymentStatus.map((status, index) => ({
      ...status,
      selection: index === statusIndex ? !status.selection : false,
    }));
    setPaymentStatus(newStatusOptions);
  };

  const handlePaymentMethodChange = (methodIndex) => {
    const newMethodOptions = paymentMethod.map((method, index) => ({
      ...method,
      selection: index === methodIndex ? !method.selection : false,
    }));
    setPaymentMethod(newMethodOptions);
  };

  // Location Type (checkboxes)
  const [structureType, setStructureType] = useState({
    residential: false,
    commercial: false,
    industrial: false,
  });

  const handleStructureTypeChange = (field) => (event) => {
    setStructureType((prev) => ({ ...prev, [field]: event.target.checked }));
  };

  // Structure Details (text fields)
  const [structureDetails, setStructureDetails] = useState({
    bedrooms: "",
    restrooms: "",
    floors: "",
  });

  const handleStructureDetailsChange = (field) => (e) => {
    const v = e.target.value;
    if (v === "" || /^[0-9]+$/.test(v)) {
      setStructureDetails(prev => ({ ...prev, [field]: v }));
    }
  };

  // Termite Activity (mutually exclusive checkboxes)
  const [termiteActivity, setTermiteActivity] = useState(""); // "", "with", "without"
  const handleTermiteActivity = (value) => () => {
    setTermiteActivity(prev => (prev === value ? "" : value));
  };

  const resizeAndUploadSignature = async (signatureRef, jobOrderId, type) => {
    // 1. grab the trimmed canvas
    if (!signatureRef.current) throw new Error("No signature canvas available");
    const canvas = signatureRef.current.getTrimmedCanvas();
    if (!canvas) throw new Error("Nothing to upload");

    // 2. draw it into a fixed‐size canvas
    const size = 200;
    const c2 = document.createElement("canvas");
    c2.width = size;
    c2.height = size;
    c2.getContext("2d").drawImage(canvas, 0, 0, size, size);

    // 3. turn into a blob, rejecting if null
    const blob = await new Promise((resolve, reject) => {
      c2.toBlob(b => (b ? resolve(b) : reject(new Error("Canvas toBlob returned null"))), "image/png");
    });

    // 4. upload
    const key = `${jobOrderId}-signature-${type}.png`;
    const params = {
      Bucket: TEMP_BUCKET_NAME,
      Key: key,
      Body: blob,
      ContentType: "image/png",
    };
    console.log("Uploading signature:", params);

    const resp = await s3TempBucketUploader.upload(params).promise();
    // AWS SDK v2 returns resp.Location; if not, you can fall back to the public URL:
    return resp.Location || `https://${params.Bucket}.s3.amazonaws.com/${params.Key}`;
  };

  // const handleGeneratePDF = async (type) => {
  //   // Merge contact details with jobOrderParameters
  //   const pdfData = {
  //     ...jobOrderParameters,
  //     accomplishedSignature: accomplishedSignatureURL,
  //     acknowledgedSignature: acknowledgedSignatureURL,
  //   };

  //   console.log("Job Order Parameters:", pdfData);

  //   try {
  //     setIsLoading(true);
  //     const pdfBlob =
  //       type === "invoice"
  //         ? await generateReceipts(pdfData)
  //         : await generateReports(pdfData);
  //     if (!pdfBlob) {
  //       throw new Error("Failed to generate PDF blob.");
  //     }
  //     const s3Url = await uploadPDF(pdfBlob, jobOrder.id, type);
  //     console.log(`${type} PDF uploaded to S3 and DynamoDB updated:`, s3Url);

  //     // Prepare update data with the correct link field
  //     const updateData = {
  //       [`${type === "invoice" ? "receipts_url" : "reports_url"}`]: s3Url,
  //     };

  //     // Update DynamoDB with the S3 URL
  //     await patchJobOrderLink(jobOrder.id, updateData);
  //     console.log(`DynamoDB updated with ${type} URL:`, s3Url);
  //   } catch (error) {
  //     console.error("Error generating or uploading PDF:", error);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  // const uploadPDF = async (pdfBlob, jobOrderId, type) => {
  //   console.log("Generated PDF Blob:", pdfBlob);

  //   const fileName = `${jobOrderId}-${type}.pdf`;
  //   const s3Uploader =
  //     type === "invoice" ? s3InvoicePDFUploader : s3ReportsPDFUploader;
  //   const params = {
  //     Bucket: type === "invoice" ? INVOICE_BUCKET_NAME : REPORTS_BUCKET_NAME,
  //     Key: `${fileName}`,
  //     Body: pdfBlob,
  //     ContentType: "application/pdf",
  //   };

  //   console.log("S3 Upload Parameters:", params); // Debugging line to view upload parameters

  //   try {
  //     await s3Uploader.upload(params).promise();
  //     return `https://${params.Bucket}.s3.amazonaws.com/${fileName}`;
  //   } catch (error) {
  //     console.error("S3 upload error:", error);
  //     throw new Error("Upload failed");
  //   }
  // }; 

  // Function to format date
  // TODO: you can use date-fns for more advanced date formatting
  function formatDate(dateString) {
    try {
      const options = { year: "numeric", month: "long", day: "numeric" };
      const date = new Date(dateString);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        throw new Error("Invalid date format");
      }

      return new Intl.DateTimeFormat("en-US", options).format(date);
    } catch (error) {
      console.error(error.message);
      return "Invalid Date"; // Fallback for invalid dates
    }
  }

  const formattedDate = formatDate(jobOrderParameters.job_date);

  const handleSendEmail = async (attachmentLinks) => {
    try {
      const recipientEmail = jobOrder?.client?.contact?.email?.email;

      if (!recipientEmail) {
        console.warn("⚠️ Email notification skipped - no client email address");
        return;
      }

      const htmlBody = buildServiceReportEmail({
        formattedDate,
        attachmentLinks,
      });

      const lambdaPayload = {
        from: "boaz-reminders@maestrosystemsinc.com",
        recipients: [recipientEmail],
        subject: `Service Report for Job Order ${jobOrder?.id}`,
        body: htmlBody,
      };

      await sendEmailQueue(lambdaPayload);
      console.log("✅ Email notification sent successfully");
    } catch (error) {
      console.warn(
        "⚠️ Email notification failed (non-critical):",
        error?.message || error
      );
    }
  };

  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  // Determine numeric dimensions based on breakpoints
  let canvasWidth, canvasHeight;
  if (isLg) {
    canvasWidth = 300;
    canvasHeight = 350;
  } else if (isMd) {
    canvasWidth = 250;
    canvasHeight = 325;
  } else {
    canvasWidth = 200;
    canvasHeight = 300;
  }

  let ackWidth, ackHeight;
  if (isLg) {
    ackWidth = 300;
    ackHeight = 150;
  } else if (isMd) {
    ackWidth = 250;
    ackHeight = 125;
  } else {
    ackWidth = 200;
    ackHeight = 100;
  }

  const onSubmit = async (formData) => {
    setIsLoading(true);

    const locations = formData?.locations || [];

    // Build at least 19 rows
    const inspection_report = Array.from({ length: Math.max(19, locations.length) }).map((_, idx) => {
      const loc = locations[idx] || {
        location: "",
        location_other: "",
        findings: "",
        action_taken: "",
        bait_monitor_location: "",
        remarks: "",
      };

      const location =
        loc.location === "Other" ? (loc.location_other || "") : (loc.location || "");
      const findings = (loc.findings || "").trim();

      // human-readable action taken
      const action_taken =
        loc.action_taken === "baiting"
          ? `Baiting System Installation/ AG Monitoring in ${loc.bait_monitor_location || ""}`.trim()
          : ({ soil: "Soil Treatment", spraying: "Spraying", monitoring: "Monitoring only" }[loc.action_taken] || "");

      const remarks = (loc.remarks || "").trim();

      return { location, findings, action_taken, remarks };
    });

    const updatedJobOrderParameters = {
      ...jobOrderParameters,
      inspection_report,
      chemical_used,
      equipment_used,
      service_report_payment_status: paymentStatus,
      service_report_payment_method: paymentMethod,
      accomplishedSignature: formData.accomplishedSignature || accomplishedSignatureURL,
      acknowledgedSignature: formData.acknowledgedSignature || acknowledgedSignatureURL,
      acknowledgedClientSignatory: acknowledgedClientSignatory,
      acknowledgedClientSignatory: formData.acknowledgedClientSignatory,
      technician_signatory: formData.technician_signatory,
      satisfaction_rating: formData.satisfaction_rating ?? jobOrderParameters.satisfaction_rating,
      client_satisfaction_remarks: jobOrderParameters.client_satisfaction_remarks,
      structure_type: {
        residential: structureType.residential,
        commercial: structureType.commercial,
        industrial: structureType.industrial,
      },
      structure_details: {
        bedrooms: structureDetails.bedrooms,
        restrooms: structureDetails.restrooms,
        floors: structureDetails.floors,
      },
      service_report_status: "Done",
      technician_signatory: technicianSignatory,
      termite_activity: termiteActivity || "",
      termite_recommendations: formData.termite_recommendations || [],
      technicians: jobOrder?.technicians || [],
      // ⏰ TIMESTAMP TRACKING (prevents technician time-cheating)
      technician_signature_uploaded_at: technicianSignatureUploadedAt,  // When technician signs
      client_signature_uploaded_at: clientSignatureUploadedAt,  // When UPLOAD was clicked
      report_submitted_at: new Date().toISOString(),  // When SUBMIT was clicked
    };

    // Exterra (cap at 8 just in case)
    const { exterra_bait_stations = [] } = formData;
    updatedJobOrderParameters.exterra_bait_stations = exterra_bait_stations.slice(0, 8);

    const { id, ...putClientParameters } = editedClient;

    //Added for Document Generation Receipts, Reports, Certificate
    try {
      // 1) transform & convert booleans
      const payload = convertBooleans({ jobOrder, updatedJobOrderParameters, form: formData });
      // 2) call your PDF “field-mapper”
      const response = await submitDocumentFieldMapper(payload);
      console.log("Job order parameters submitted successfully:", response);

      const generatedLinks = buildGeneratedDocumentLinks(jobOrder.id, {
        includeReport: false,
        includeCertificate: false,
        includeReceipt: true,
      });

      setLinks(generatedLinks);

      // Send the email after updating links

      setSuccessMessage("Service Report and documents generated successfully.");

      setIsLoading(false);
      await handleSendEmail(generatedLinks);

      // 💾 SAVE ALL UPDATED PARAMETERS TO DATABASE (including timestamps)
      await putJobOrder(jobOrder.id, updatedJobOrderParameters);
      await putClient(id, putClientParameters);
    } catch (error) {
      console.error("Error submitting job order parameters:", error);
      setErrorMessage(
        `Failed to generate Service Report and Documents: ${error.message}. Please check your network connection and try again.`
      );
    } finally {
      setIsLoading(false); // Set loading to false
    }
  }

  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon sx={{ fontSize: "40px", marginRight: "8px" }} />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="Create Service Report"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="job-order-form">
        <Grid container>
          {/* Page 1 - Review Job Details */}
          {currentStep === STEP_INDEX.REVIEW_JOB_DETAILS && (
            <>
              <StepHeaderCard
                title="Review Job Details"
                subtitle="Review the job details before proceeding to the termite inspection report."
              />

              <PageOneReviewSection
                jobOrder={jobOrder}
                client={client}
                selectedJobTypes={selectedJobTypes}
                selectedJobDescriptions={selectedJobDescriptions}
                jobOrderParameters={jobOrderParameters}
                errors={errors}
                onStatusChange={(value) => handleJObOrderChange("status", value)}
                showStatus={true}
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={false}
                showCancel={true}
                showNext={true}
                onCancel={handleCancel}
                onNext={handleNextFromReviewJobDetails}
              />
            </>
          )}

          {/* Page 2 - Inspection Report */}
          {currentStep === STEP_INDEX.INSPECTION_REPORT && (
            <>
              <StepHeaderCard
                title="Inspection Report"
                subtitle="Complete the termite inspection findings before proceeding."
              />

              {/* Structure Type Section */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardHeader
                    title={
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                      >
                        Structure Type
                      </Typography>
                    }
                    sx={{ pb: 0, px: 2, pt: 2 }}
                  />
                  <CardContent sx={{ px: 2, pb: 2 }}>
                    <FormGroup sx={{ width: "100%" }}>
                      <FormControlLabel
                        sx={{ width: "100%" }}
                        control={
                          <Checkbox
                            checked={structureType.residential}
                            onChange={handleStructureTypeChange("residential")}
                          />
                        }
                        label="Residential"
                      />
                      <FormControlLabel
                        sx={{ width: "100%" }}
                        control={
                          <Checkbox
                            checked={structureType.commercial}
                            onChange={handleStructureTypeChange("commercial")}
                          />
                        }
                        label="Commercial"
                      />
                      <FormControlLabel
                        sx={{ width: "100%" }}
                        control={
                          <Checkbox
                            checked={structureType.industrial}
                            onChange={handleStructureTypeChange("industrial")}
                          />
                        }
                        label="Industrial"
                      />
                    </FormGroup>
                  </CardContent>
                </Card>
              </Grid>

              {/* Structure Details Section */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardHeader
                    title={
                      <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                        Structure Details
                      </Typography>
                    }
                    sx={{ pb: 0, px: 2, pt: 2 }}
                  />
                  <CardContent sx={{ px: 2, pb: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Number of Bedrooms"
                          type="number"
                          value={structureDetails.bedrooms}
                          onChange={handleStructureDetailsChange("bedrooms")}
                          fullWidth
                          size="small"
                          inputProps={{ min: 0 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Number of Restrooms"
                          type="number"
                          value={structureDetails.restrooms}
                          onChange={handleStructureDetailsChange("restrooms")}
                          fullWidth
                          size="small"
                          inputProps={{ min: 0 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Number of Floors"
                          type="number"
                          value={structureDetails.floors}
                          onChange={handleStructureDetailsChange("floors")}
                          fullWidth
                          size="small"
                          inputProps={{ min: 0 }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Termite Activity */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardHeader
                    title={
                      <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                        Termite Activity
                      </Typography>
                    }
                    sx={{ pb: 0, px: 2, pt: 2 }}
                  />
                  <CardContent sx={{ px: 2, pb: 2 }}>
                    <FormGroup sx={{ width: "100%" }}>
                      <FormControlLabel
                        sx={{ width: "100%" }}
                        control={
                          <Checkbox
                            checked={termiteActivity === "with"}
                            onChange={handleTermiteActivity("with")}
                          />
                        }
                        label="With Active Termites"
                      />
                      <FormControlLabel
                        sx={{ width: "100%" }}
                        control={
                          <Checkbox
                            checked={termiteActivity === "without"}
                            onChange={handleTermiteActivity("without")}
                          />
                        }
                        label="Without Active Termites"
                      />
                    </FormGroup>
                  </CardContent>
                </Card>
              </Grid>

              {/* Inspection Report (Mobile) */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardHeader
                    title={
                      <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                        Inspection Report
                      </Typography>
                    }
                    sx={{ pb: 0, px: 2, pt: 2 }}
                  />
                  <CardContent sx={{ px: 2, pb: 2 }}>
                    <Stack spacing={2}>
                      {locationFields.map((loc, idx) => (
                        <TermiteLocationCard
                          key={loc.id}
                          control={control}
                          register={register}
                          index={idx}
                          removeLocation={removeLocation}
                        />
                      ))}

                      <Button
                        variant="contained"
                        disabled={locationFields.length >= 19}
                        fullWidth
                        onClick={() =>
                          appendLocation({
                            location: "",
                            location_other: "",
                            findings: "",
                            action_taken: "",
                            bait_monitor_location: "",
                            remarks: "",
                          })
                        }
                      >
                        Add New Location ({locationFields.length}/19)
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <StepActionBar
                isLoading={isLoading}
                showBack={true}
                showCancel={true}
                showNext={true}
                onBack={() => goBackToStep(STEP_INDEX.REVIEW_JOB_DETAILS)}
                onCancel={handleCancel}
                onNext={handleNextFromInspectionReport}
              />
            </>
          )}

          {/* Page 3 - Recommendations and Notes */}
          {currentStep === STEP_INDEX.RECOMMENDATIONS_AND_NOTES && (
            <>
              <StepHeaderCard
                title="Recommendations and Notes"
                subtitle="Complete the termite recommendations, Exterra details, chemical usage, and reminder notes before proceeding."
              />

              {/* Recommendations Section */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ mb: 2, mt: 2 }}>
                  <CardHeader
                    title={
                      <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                        Recommendations
                      </Typography>
                    }
                    sx={{ pb: 0, px: 2, pt: 2 }}
                  />
                  <CardContent sx={{ px: 2, pb: 2 }}>
                    <FormGroup sx={{ width: "100%" }}>
                      {RECOMMENDATIONS.map((label) => (
                        <Controller
                          key={label}
                          name="termite_recommendations"
                          control={control}
                          render={({ field: { value = [], onChange } }) => {
                            const checked = value.includes(label);
                            return (
                              <FormControlLabel
                                sx={{ width: "100%" }}
                                control={
                                  <Checkbox
                                    checked={checked}
                                    onChange={(e) =>
                                      onChange(
                                        e.target.checked
                                          ? [...value, label]
                                          : value.filter((v) => v !== label)
                                      )
                                    }
                                  />
                                }
                                label={label}
                              />
                            );
                          }}
                        />
                      ))}
                    </FormGroup>
                    {errors?.termite_recommendations && (
                      <FormHelperText error sx={{ mt: 1 }}>
                        {errors.termite_recommendations.message}
                      </FormHelperText>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Exterra Section */}
              <Grid item xs={12}>
                <ExterraCard control={control} register={register} />
              </Grid>

              {/* Chemical Used Section */}
              <Grid
                item
                xs={12}
                md={12}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <Card
                  variant="outlined"
                  sx={{
                    marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                    flex: 1,
                  }}
                >
                  <CardContent sx={{ flex: 1 }}>
                    <Grid item xs={12}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell
                                align="center"
                                colSpan={2}
                                style={{ fontWeight: "bold" }}
                              >
                                Chemical Used / Batch # / Expiration
                              </TableCell>
                              <TableCell
                                align="center"
                                style={{ fontWeight: "bold" }}
                              >
                                Dilution
                              </TableCell>
                              <TableCell
                                align="center"
                                colSpan={2}
                                style={{ fontWeight: "bold" }}
                              >
                                Equipment Used
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {chemical_used.map((chemical, index) => (
                              <TableRow key={index}>
                                <TableCell>{chemical.label}:</TableCell>
                                <TableCell>
                                  <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={chemical.chemical}
                                    onChange={(e) =>
                                      handleChemicalInputChange(
                                        index,
                                        "chemical",
                                        e.target.value
                                      )
                                    }
                                    sx={{ minWidth: 100 }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={chemical.dilution}
                                    onChange={(e) =>
                                      handleChemicalInputChange(
                                        index,
                                        "dilution",
                                        e.target.value
                                      )
                                    }
                                    sx={{ minWidth: 50 }}
                                  />
                                </TableCell>
                                {index < equipment_used.length ? (
                                  <>
                                    <TableCell>
                                      {equipment_used[index].label}
                                    </TableCell>
                                    <TableCell>
                                      <Checkbox
                                        checked={equipment_used[index].usage}
                                        onChange={() =>
                                          handleEquipmentCheckboxChange(index)
                                        }
                                      />
                                    </TableCell>
                                  </>
                                ) : (
                                  <>
                                    <TableCell />
                                    <TableCell />
                                  </>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Reminder Notes */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardHeader
                    title={
                      <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                        Reminder Notes
                      </Typography>
                    }
                    sx={{ pb: 0, px: 2, pt: 2 }}
                  />
                  <CardContent sx={{ px: 2, pb: 2 }}>
                    <TextField
                      label="Reminder Notes"
                      value={jobOrderParameters?.reminder_notes || ""}
                      onChange={(e) =>
                        handleJObOrderChange("reminder_notes", e.target.value)
                      }
                      fullWidth
                      multiline
                      rows={6}
                      size="small"
                    />
                  </CardContent>
                </Card>
              </Grid>

              <StepActionBar
                isLoading={isLoading}
                showBack={true}
                showCancel={true}
                showNext={true}
                onBack={() => goBackToStep(STEP_INDEX.INSPECTION_REPORT)}
                onCancel={handleCancel}
                onNext={handleNextFromRecommendationsAndNotes}
              />
            </>
          )}

          {/* Page 4 - Payment Section */}
          {currentStep === STEP_INDEX.PAYMENT_DETAILS && (
            <>
              <StepHeaderCard
                title="Payment Details"
                subtitle="Review and update service fee, payment status, and payment method."
              />

              <PaymentDetailsSection
                serviceFee={jobOrderParameters?.service_fee}
                onServiceFeeChange={(value) =>
                  handleJObOrderChange("service_fee", value)
                }
                paymentStatusOptions={paymentStatus}
                paymentMethodOptions={paymentMethod}
                onPaymentStatusChange={handlePaymentStatusChange}
                onPaymentMethodChange={handlePaymentMethodChange}
                readOnly={false}
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={true}
                showCancel={true}
                showNext={true}
                onBack={() => goBackToStep(STEP_INDEX.RECOMMENDATIONS_AND_NOTES)}
                onCancel={handleCancel}
                onNext={handleNextFromPaymentDetails}
              />
            </>
          )}
          
          {/* Page 5 - Technician Only */}
          {currentStep === STEP_INDEX.TECHNICIAN_ONLY && (
            <>
              <StepHeaderCard
                title="Technician Only"
                subtitle="Confirm the technician roster and sign the form."
              />

              <TechnicianSignatureCard
                accomplishedBySignatureRef={accomplishedBySignatureRef}
                canvasWidth={canvasWidth}
                ackHeight={ackHeight}
                technicianSignatory={technicianSignatory}
                setTechnicianSignatory={setTechnicianSignatory}
                setValue={setValue}
                clearErrors={clearErrors}
                errors={errors}
                jobOrder={jobOrder}
                clearAccomplishedSignature={clearAccomplishedSignature}
                saveAndUploadAccomplishedSignature={saveAndUploadAccomplishedSignature}
                isLoading={isLoading}
                isTechnician={isTechnician}
                technicianSignatureUploadedAt={technicianSignatureUploadedAt}
                register={register}
                openEditTechs={openEditTechs}
                jobDate={jobOrderParameters?.job_date || ""}
                startTime={jobOrderParameters?.start_time || ""}
                showSchedule={true}
                readOnlySchedule={true}
                onJobDateChange={(value) => handleJObOrderChange("job_date", value)}
                onStartTimeChange={(value) => handleJObOrderChange("start_time", value)}
                allowEditTechnicians={true}
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={true}
                showCancel={true}
                showNext={true}
                onBack={() => goBackToStep(STEP_INDEX.PAYMENT_DETAILS)}
                onCancel={handleCancel}
                onNext={handleNextFromTechnicianOnly}
              />
            </>
          )}
          
          {/* Dialog for Edit Technicians */}
          <EditTechniciansDialog
            open={editTechsOpen}
            onClose={closeEditTechs}
            onSave={saveEditTechs}
            editedTechs={editedTechs}
            techniciansList={allTechnicians}
            onChangeTech={handleChangeEditedTech}
            onAddTech={handleAddEditedTech}
            onRemoveTech={handleRemoveEditedTech}
            isLoading={isLoading}
          />

          {/* Page 6 - Client Signature */}
          {currentStep === STEP_INDEX.CLIENT_SIGNATURE && (
            <>
              <StepHeaderCard
                title="For Client Only"
                subtitle="Please sign the form and provide your satisfaction rating on the next page."
              />

              <ClientSignatureCard
                acknowledgedClientSignatory={acknowledgedClientSignatory}
                setAcknowledgedClientSignatory={setAcknowledgedClientSignatory}
                setValue={setValue}
                clearErrors={clearErrors}
                errors={errors}
                acknowledgedBySignatureRef={acknowledgedBySignatureRef}
                canvasWidth={ackWidth}
                ackHeight={ackHeight}
                saveAndUploadAcknowledgedSignature={saveAndUploadAcknowledgedSignature}
                clearAcknowledgedSignature={clearAcknowledgedSignature}
                isLoading={isLoading}
                isTechnician={isTechnician}
                clientSignatureUploadedAt={clientSignatureUploadedAt}
                readOnly={false}
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={false}
                showCancel={false}
                showNext={true}
                onBack={() => goBackToStep(STEP_INDEX.TECHNICIAN_ONLY)}
                onCancel={handleCancel}
                onNext={handleNextFromClientSignature}
              />
            </>
          )}

          {/* Page 7 - Client Satisfaction Rating */}
          {currentStep === STEP_INDEX.CLIENT_SATISFACTION && (
            <>
              <StepHeaderCard
                title="For Client Only"
                subtitle="Your satisfaction and feedback are important to us."
              />

              <ClientSatisfactionCard
                rating={satisfactionRating}
                remarks={jobOrderParameters?.client_satisfaction_remarks || ""}
                onRatingChange={(val) => {
                  setSatisfactionRating(val);
                  setjobOrderParameters((prev) => ({
                    ...prev,
                    satisfaction_rating: val,
                  }));
                  setValue("satisfaction_rating", val, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
                onRemarksChange={(val) =>
                  handleJObOrderChange("client_satisfaction_remarks", val)
                }
                ratingError={errors?.satisfaction_rating?.message}
                readOnly={false}
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={false}
                showCancel={false}
                showNext={true}
                onBack={() => goBackToStep(STEP_INDEX.CLIENT_SIGNATURE)}
                onCancel={handleCancel}
                onNext={handleNextFromClientSatisfaction}
              />
            </>
          )}

          {/* Page 8 - Last Page */}
          {currentStep === STEP_INDEX.FINAL_REVIEW && (
            <>
              <StepHeaderCard
                title="Final Review & Submit"
                subtitle="Submit the report and review the generated document links."
              />

              <DocumentLinksCard links={links} />

              <StepActionBar
                isLoading={isLoading}
                showBack={false}
                showCancel={!reportGenerated}
                showSubmit={!reportGenerated}
                showClose={reportGenerated}
                onCancel={handleCancel}
                onClose={handleClose}
                onSubmitClick={handleSubmit(onSubmit, onInvalid)}
              />
            </>
          )}
        </Grid>
      </form>
    </BaseFormWrapper>
  );
}

export default TermiteControlForm;
